import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HedgehogLogo } from '../components/brand/HedgehogLogo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { AlertCircle, Lock, Mail, User, Building2, ArrowRight, Check } from 'lucide-react';

export const RegisterView: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError('Please provide your full name.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Please provide a valid email address.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!organizationName.trim()) {
      setError('Please provide your organization or company name.');
      return;
    }

    setIsLoading(true);
    try {
      await register({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        password,
        organization_name: organizationName.trim(),
      });
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Registration failed. An account with this email may already exist.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cf-bg flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="flex justify-center mb-3">
          <HedgehogLogo size={42} />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-cf-textPrimary">
          Create Your Ericius Account
        </h2>
        <p className="mt-1 text-xs text-cf-textSecondary">
          Launch a new organization with free instant form ingestion
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="bg-cf-surface py-8 px-6 border-cf-border shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-5 p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-xs text-rose-600 dark:text-rose-400 font-medium">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Full Name"
              size="xs"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              leftIcon={<User size={14} />}
            />

            <Input
              label="Work Email Address"
              size="xs"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@company.com"
              leftIcon={<Mail size={14} />}
            />

            <Input
              label="Organization Name"
              size="xs"
              type="text"
              required
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Acme Inc."
              leftIcon={<Building2 size={14} />}
            />

            <div>
              <Input
                label="Password"
                size="xs"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                leftIcon={<Lock size={14} />}
              />
              <p className="mt-1 text-xs text-cf-textMuted flex items-center gap-1">
                <Check
                  size={11}
                  className={
                    password.length >= 8
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-cf-textMuted'
                  }
                />
                <span>Must be at least 8 characters</span>
              </p>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                fullWidth
                disabled={isLoading}
                isLoading={isLoading}
                rightIcon={!isLoading ? <ArrowRight size={14} /> : undefined}
              >
                Create Organization
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-5 border-t border-cf-border text-center">
            <p className="text-xs text-cf-textSecondary">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-cf-orange hover:text-cf-orangeHover font-medium transition-colors"
              >
                Sign in directly
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
